import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { PersonalNumberRange } from 'features/care-connect-settings/domain/models/personal-number-range';
import { ReadPersonalNumberRangesResponse } from 'features/care-connect-settings/domain/models/personal-number-range-response';
import { UpsertPersonalNumberRangeCommand } from 'features/care-connect-settings/domain/models/upsert-personal-number-range-command';

const personalNumberRangeBaseUrl = '/api/v1/PersonalNumberRange';

export enum PersonalNumberRangeQueryPaths {
  ReadPersonalNumberRanges = 'ReadPersonalNumberRanges',
  UpsertPersonalNumberRange = 'UpsertPersonalNumberRange',
  RemovePersonalNumberRange = 'RemovePersonalNumberRange',
}

const readPersonalNumberRanges = async (axiosInstance: AxiosInstance): Promise<ReadPersonalNumberRangesResponse> => {
  const response = await axiosInstance.get(`${personalNumberRangeBaseUrl}/${PersonalNumberRangeQueryPaths.ReadPersonalNumberRanges}`);
  return response.data;
};

const upsertPersonalNumberRange = async (
  axiosInstance: AxiosInstance,
  command: UpsertPersonalNumberRangeCommand,
): Promise<PersonalNumberRange> => {
  const response = await axiosInstance.post(
    `${personalNumberRangeBaseUrl}/${PersonalNumberRangeQueryPaths.UpsertPersonalNumberRange}`,
    command,
  );
  return response.data;
};

const removePersonalNumberRange = async (axiosInstance: AxiosInstance, id: string): Promise<void> => {
  await axiosInstance.delete(`${personalNumberRangeBaseUrl}/${PersonalNumberRangeQueryPaths.RemovePersonalNumberRange}/${id}`);
};

export const useReadPersonalNumberRanges = (enabled: boolean) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [PersonalNumberRangeQueryPaths.ReadPersonalNumberRanges],
    queryFn: () => readPersonalNumberRanges(authenticatedInstance),
    enabled,
  });
};

export const useUpsertPersonalNumberRange = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationFn: (command: UpsertPersonalNumberRangeCommand) => upsertPersonalNumberRange(authenticatedInstance, command),
  });
};

export const useRemovePersonalNumberRange = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationFn: (id: string) => removePersonalNumberRange(authenticatedInstance, id),
  });
};
